import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { MESSAGES } from "@/modules/shared/constants/messages";
import { useToast } from "@/modules/shared/hooks/use-toast";
import type { MutationRequest } from "@/modules/shared/types/mutation-request.type";
import type { ResponseAPI } from "@/modules/shared/types/response-api.type";

import { useLogger } from "@/modules/shared/hooks/use-logger.hook";
import { useOrderFormStore } from "../stores/order-form.store";
import { type IOrderFormSaveService, orderFormSaveService } from "../services/order-form-save.service";

export const useSaveOrderMutation = ({
	callback,
	resolveRequest,
}: MutationRequest) => {
	const { toast } = useToast();
	const { log } = useLogger();
	const { operationType } = useOrderFormStore();
	const { mutateAsync: saveOrderFn, isLoading: isSavingOrder } = useMutation({
		mutationFn: async (data: IOrderFormSaveService) =>
			await orderFormSaveService({
				jsonOrderForm: data.jsonOrderForm,
			}),
		onSuccess: async (data, variables) => {
			const idPedido =
				variables?.jsonOrderForm?.idPedidoCel ||
				variables?.jsonOrderForm?.idPedido;
			if (data?.status && data.status >= 200 && data.status < 300) {
				log(
					"success",
					operationType === "add" ? "order-save" : "order-update",
					operationType === "add"
						? `Pedido  #${idPedido} criado com sucesso
					`
						: `Pedido #${idPedido} atualizado com sucesso`,
					{
						response: data,
						payload: variables.jsonOrderForm,
					},
				);
			} else {
				log(
					"error",
					operationType === "add" ? "order-save" : "order-update",
					operationType === "add"
						? ` Erro ao criar pedido #${idPedido}`
						: `Erro ao atualizar pedido #${idPedido}`,
					{
						response: data,
						payload: variables?.jsonOrderForm,
					},
				);
			}
			resolveRequest({
				callback: data.status === 200 || data.status === 203 ? callback : null,
				status: data.status,
				showToastInfo: true,
				message: data.message,
				showToastSuccess: true,
			});
		},
		onError: (error: AxiosError<ResponseAPI>, variables) => {
			const idPedido =
				variables?.jsonOrderForm?.idPedidoCel ||
				variables?.jsonOrderForm?.idPedido;
			log(
				"error",
				operationType === "add" ? "order-save" : "order-update",
				operationType === "add"
					? `Erro ao criar pedido #${idPedido}`
					: `Erro ao atualizar pedido #${idPedido}`,
				{
					error,
					payload: variables.jsonOrderForm,
				},
			);
			if (
				error.response?.data?.status > 400 &&
				error.response?.data?.status < 500 &&
				error.response?.data?.message
			) {
				toast.info({
					title: error.response.data.message,
				});
			} else {
				toast.error({
					title: MESSAGES.ERROR_TITLE,
					description: MESSAGES.ERROR_DESCRIPTION,
					action: {
						label: MESSAGES.RELOAD,
						onClick: () => {
							window.location.reload();
						},
					},
				});
			}
		},
	});

	return {
		saveOrderFn,
		isSavingOrder,
	};
};
